import { Button, Flex, Text } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { UpdateApplicationBlock } from "@/components/ApplicationBlocks/UpdateApplicationBlock";
import { Layout } from "@/components/Layout/Layout";
import { RootState, useAppDispatch, useAppSelector } from "@/store";
import { useEffect, useMemo } from "react";
import { fetchCustomers } from "@/store/slices/customers";
import { fetchSingleApplication, fetchUpdateApplication } from "@/store/slices/applications";
import { fetchAllProjects } from "@/store/slices/projects";
import { ApplicationForm } from "@/model/applications";
import { useFormik } from "formik";
import PageLoaderWrapper from "@/components/PageLoaderWrapper";
import { normalizeApplicationForm } from "@/helpers/normalizeApplicationForm";
import { SortingTypes } from "@/helpers/SortingTypes";

const getDefaultPaginatedListFilters = (prefix: string) =>
  ({
    limit: "100000",
    offset: "0",
    sort_column: `${prefix}.created_at`,
    sort_direction: SortingTypes.DESC,
  }) as const;

export const UpdateApplicationPage = () => {
  const { currentApplication, isLoading } = useAppSelector((state: RootState) => state.applications);

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { id } = useParams();

  useEffect(() => {
    dispatch(fetchSingleApplication({ params: { id: Number(id) } }));
  }, [dispatch, id]);

  const formik = useFormik<ApplicationForm>({
    initialValues: {
      name: "",
      customer_id: null,
      project_id: null,
      legal_entity_id: null,
      profile_id: null,
      candidate_grade: [],
      candidate_requirements: "",
      job_duties: "",
      project_description: "",
      bitrix_contract_link: "",
      sales_max_offer_general: "",
      sales_max_offer_regions: "",
      sales_max_offer_moscow: "",
      marginality: null,
      purchase_rate_max: "",
      required_number: null,
      location: "",
      job_format: "",
      office_address: "",
      job_workload: "",
      attraction_period: "",
      comments: "",
      attachments: [],
      attachments_delete: [],
      status: 0 as any,
    },
    validate: (values) => {
      const errors: any = {};

      const isNonEmptyString = (v: any) => typeof v === "string" && v.trim().length > 0;
      const isPresentId = (v: any) => v !== null && v !== undefined && !(typeof v === "string" && v.trim() === "");

      if (!isNonEmptyString(values.name)) errors.name = true;
      if (!isPresentId(values.customer_id)) errors.customer_id = true;
      if (!isPresentId(values.profile_id)) errors.profile_id = true;
      if (!isPresentId(values.legal_entity_id)) errors.legal_entity_id = true;
      if (!Array.isArray(values.candidate_grade) || values.candidate_grade.length === 0) errors.candidate_grade = true;
      if (!isNonEmptyString(values.candidate_requirements)) errors.candidate_requirements = true;
      if (!isNonEmptyString(values.job_duties)) errors.job_duties = true;
      if (!isNonEmptyString(values.project_description)) errors.project_description = true;

      return errors;
    },
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: () => {},
  });

  const initialApplicationValues = useMemo(() => {
    if (!currentApplication) return null;
    return {
      name: currentApplication.name ?? "",
      customer_id: currentApplication.customer?.id ?? null,
      project_id: currentApplication.project?.id ?? null,
      legal_entity_id: currentApplication.legal_entity?.id ?? null,
      profile_id: currentApplication.profile?.id ?? null,
      candidate_grade: currentApplication.candidate_grade?.map((el: any) => String(el.id)) ?? [],
      candidate_requirements: currentApplication.candidate_requirements ?? "",
      job_duties: currentApplication.job_duties ?? "",
      project_description: currentApplication.project_description ?? "",
      bitrix_contract_link: (currentApplication as any).bitrix_contract_link ?? "",
      sales_max_offer_general: (currentApplication as any).sales_max_offer_general || "",
      sales_max_offer_regions: currentApplication.sales_max_offer_regions || "",
      sales_max_offer_moscow: currentApplication.sales_max_offer_moscow || "",
      marginality: currentApplication.marginality ?? null,
      purchase_rate_max: (currentApplication as any).purchase_rate_max || "",
      required_number: ((): number | null => {
        const v = (currentApplication as any).required_number;
        if (v === undefined || v === null) return null;
        const s = String(v).trim();
        if (s === "") return null;
        const n = Number(s);
        return Number.isNaN(n) ? null : n;
      })(),
      location: currentApplication.location ?? "",
      job_format: String(currentApplication.job_format?.id ?? ""),
      office_address: currentApplication.office_address ?? "",
      job_workload: String(currentApplication.job_workload?.id ?? ""),
      attraction_period: String(currentApplication.attraction_period?.id ?? ""),
      comments: currentApplication.comments ?? "",
      attachments: [],
      attachments_delete: [],
      status: Number((currentApplication as any)?.status?.id ?? (currentApplication as any)?.status_id ?? 0),
    };
  }, [currentApplication]);

  const hasChanges = useMemo(() => {
    if (!initialApplicationValues) return false;

    const current = formik.values;
    const initial = initialApplicationValues;

    // Проверяем файлы отдельно
    if (current.attachments.length > 0 || current.attachments_delete.length > 0) {
      return true;
    }

    // Проверяем все остальные поля автоматически
    const fieldsToCheck = Object.keys(initial).filter((key) => key !== "attachments" && key !== "attachments_delete") as Array<keyof ApplicationForm>;

    return fieldsToCheck.some((field) => {
      const currentValue = current[field];
      const initialValue = initial[field];

      // Специальная обработка для массивов (candidate_grade)
      if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
        return JSON.stringify([...currentValue].sort()) !== JSON.stringify([...initialValue].sort());
      }

      return currentValue !== initialValue;
    });
  }, [formik.values, initialApplicationValues]);

  useEffect(() => {
    if (initialApplicationValues && !formik.dirty) {
      formik.resetForm({ values: initialApplicationValues });
    }
  }, [initialApplicationValues, formik.dirty]);

  useEffect(() => {
    dispatch(fetchCustomers({ params: { filters: getDefaultPaginatedListFilters("customer") } }));
    dispatch(fetchAllProjects({ params: { filters: getDefaultPaginatedListFilters("project") } }));
  }, [dispatch]);

  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных заявки для редактирования...">
        <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
          <Text
            color="gray.500"
            cursor="pointer"
            onClick={() => navigate(`/applications/${currentApplication?.id}`)}
            display="flex"
            alignItems="center"
            gap="8px"
          >
            <ArrowBackIcon />К заявке
          </Text>
          <Flex flexDirection="column" gap="4px">
            <Text fontSize={24} fontWeight={500}>
              Редактирование заявки
            </Text>
            <Text color="gray.500">Обновите информацию о заявке на подбор</Text>
          </Flex>
          <UpdateApplicationBlock formik={formik} />
          <Flex gap="16px" justifyContent="flex-end">
            <Button onClick={() => (currentApplication ? navigate(`/applications/${currentApplication.id}`) : navigate("/applications"))}>
              Отмена
            </Button>
            <Button
              bg="#2563eb"
              color="white"
              _hover={{ bg: "#2563eb" }}
              _active={{ bg: "#2563eb" }}
              isDisabled={!formik.isValid || !hasChanges}
              onClick={() => {
                currentApplication &&
                  dispatch(fetchUpdateApplication({ params: { id: currentApplication.id }, body: normalizeApplicationForm(formik.values) })).then(
                    (res: any) => {
                      if (res.payload.id) {
                        currentApplication && navigate(`/applications/${currentApplication.id}`);
                      }
                    },
                  );
              }}
            >
              Сохранить изменения
            </Button>
          </Flex>
        </Flex>
      </PageLoaderWrapper>
    </Layout>
  );
};
